import React, { useState, useEffect } from 'react';
import { Truck, ExternalLink, Loader2, Save, Trash2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ordersAPI } from '../services/api';

export const trackingUrl = (carriers, carrier, number) => {
  const template = carriers.find(c => c.name === carrier)?.url_template;
  return template ? template.replace('{n}', encodeURIComponent(number)) : null;
};

const TrackingForm = ({ order, onUpdated }) => {
  const [carriers, setCarriers] = useState([]);
  const [carrier, setCarrier] = useState(order.tracking_carrier || 'Colissimo');
  const [number, setNumber] = useState(order.tracking_number || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    ordersAPI.getCarriers().then(setCarriers).catch(() => setCarriers([]));
  }, []);

  const save = async () => {
    setError('');
    if (number.replace(/\s+/g, '').length < 4) { setError('Numéro de suivi trop court (4 caractères minimum)'); return; }
    setSaving(true);
    try {
      const updated = await ordersAPI.updateTracking(order.id, carrier, number);
      onUpdated(updated);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de l\'enregistrement du suivi');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Supprimer les informations de suivi ?')) return;
    setSaving(true);
    try {
      await ordersAPI.clearTracking(order.id);
      setNumber('');
      onUpdated({ ...order, tracking_carrier: null, tracking_number: null });
    } finally {
      setSaving(false);
    }
  };

  const link = order.tracking_number ? trackingUrl(carriers, order.tracking_carrier, order.tracking_number) : null;

  return (
    <div className="border border-gray-200 rounded-lg p-4" data-testid="tracking-form">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Truck size={18} className="text-orange-500" /> Suivi de colis</h3>
        {order.tracking_number && (
          <span data-testid="tracking-current" className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
            {order.tracking_carrier} · {order.tracking_number}
          </span>
        )}
      </div>
      {error && <p data-testid="tracking-error" className="text-sm text-red-600 flex items-center gap-2 mb-2"><AlertCircle size={14} /> {error}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_auto] gap-2">
        <select data-testid="tracking-carrier-select" value={carrier} onChange={(e) => setCarrier(e.target.value)} className="text-sm border rounded-md px-2 py-2 bg-white">
          {(carriers.length ? carriers : [{ name: 'Colissimo' }]).map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
        <Input data-testid="tracking-number-input" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="N° de suivi (ex : 6A12345678901)" className="h-9 text-sm" />
        <div className="flex gap-1">
          <Button data-testid="tracking-save" size="sm" onClick={save} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2d4a6f]">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          </Button>
          {order.tracking_number && (
            <Button data-testid="tracking-clear" size="sm" variant="outline" onClick={clear} disabled={saving} className="text-red-600"><Trash2 size={14} /></Button>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        {order.tracking_number
          ? <>Expédiée{order.shipped_at ? ` le ${new Date(order.shipped_at).toLocaleDateString('fr-FR')}` : ''}. {link && <a href={link} target="_blank" rel="noreferrer" data-testid="tracking-admin-link" className="text-blue-600 hover:underline inline-flex items-center gap-1">Voir chez le transporteur <ExternalLink size={12} /></a>}</>
          : 'L\'enregistrement d\'un numéro passe automatiquement la commande au statut « Expédié ».'}
      </p>
    </div>
  );
};

export default TrackingForm;
